<template>
    <div v-if="panelVm.visible"
         class="panel-app"
         :class="[panelClass, {'panel-app--floating': panelVm.floatingMode, 'panel-app--compact': panelVm.isCompact}]"
         :style="{'--panel-z-base': panelVm.panelZBase}"
         @mousedown="panelVm.onPanelFocus" @touchstart="panelVm.onPanelFocus">

        <!-- Backdrop -->
        <div v-if="!panelVm.floatingMode && !panelVm.minimized" class="panel-backdrop" @click="panelVm.close()"></div>

        <!-- Panel content -->
        <div ref="panelContent" class="panel-content" :class="[contentClass, {'panel-content--opening': panelVm.animating === 'open', 'panel-content--closing': panelVm.animating === 'close', 'panel-content--shake-hidden': panelVm.shakeHidden, 'panel-content--minimizing': panelVm.animating === 'minimize', 'panel-content--restoring': panelVm.animating === 'restore', 'panel-content--minimized': panelVm.minimized && !panelVm.animating, 'panel-content--transparent': panelVm.panelTransparent}]" :style="panelVm.panelStyle">

            <!-- Resize handles -->
            <template v-if="panelVm.floatingMode && !panelVm.isCompact && activeResizeDirections.length">
                <div v-for="dir in activeResizeDirections" :key="dir"
                     :class="'panel-resize-handle panel-resize-' + dir"
                     @mousedown="panelVm.startResize($event, dir)"
                     @touchstart.prevent="panelVm.startResize($event, dir)">
                </div>
            </template>

            <!-- Titlebar -->
            <div class="panel-titlebar" :class="{'panel-titlebar--draggable': panelVm.floatingMode}" @mousedown="panelVm.startDrag" @touchstart="panelVm.startDrag" @contextmenu.prevent="openSettingsMenu">
                <div class="panel-titlebar-left">
                    <slot name="titlebar-left"></slot>
                </div>

                <slot name="titlebar-center"></slot>

                <div class="panel-titlebar-actions">
                    <slot name="titlebar-actions"></slot>
                    <!-- Settings button -->
                    <button v-if="panelVm.showSettingsBtn" class="panel-titlebar-btn" @click.stop="openSettingsMenu($event)" :title="$t('common.settings')">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                    </button>

                    <!-- Floating toggle -->
                    <button v-if="!panelVm.isCompact" class="panel-titlebar-btn" @click.stop="panelVm.toggleFloatingMode">
                        <svg v-if="!panelVm.floatingMode" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="2" y="2" width="20" height="20" rx="2" ry="2"/><rect x="12" y="12" width="10" height="10" rx="1" ry="1" stroke="currentColor" fill="var(--panel-titlebar-bg, #f8f8f8)"/>
                        </svg>
                        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                        </svg>
                    </button>

                    <!-- Minimize -->
                    <button v-if="panelVm.floatingMode && !panelVm.isCompact" class="panel-titlebar-btn" @click.stop="panelVm.minimize()" :title="$t('common.dock.minimize')">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>

                    <!-- Close -->
                    <button class="panel-titlebar-btn panel-titlebar-btn--close" @click.stop="panelVm.close()">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
            </div>

            <!-- Body -->
            <div class="panel-body">
                <slot></slot>
            </div>
        </div>

        <!-- Settings Menu -->
        <PanelSettingsMenu ref="settingsMenu" :items="panelVm._panelSettingsItems" :z-base="panelVm.panelZBase">
            <template v-if="$slots['settings-content']" #default>
                <slot name="settings-content"></slot>
            </template>
        </PanelSettingsMenu>

        <!-- Overlays slot -->
        <slot name="overlays"></slot>
    </div>
</template>

<script>
import PanelSettingsMenu from './PanelSettingsMenu.vue'

const ALL_DIRECTIONS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw']

export default {
    name: 'PanelFrame',
    components: { PanelSettingsMenu },
    inject: ['panelVm'],
    mounted() {
        this._onTouchMove = (e) => {
            if (!this.panelVm.floatingMode) return
            const el = this.$refs.panelContent
            if (!el || !el.contains(e.target)) return
            if (!this._isInScrollableArea(e.target)) e.preventDefault()
        }
        document.addEventListener('touchmove', this._onTouchMove, { passive: false })
    },
    beforeUnmount() {
        document.removeEventListener('touchmove', this._onTouchMove)
    },
    props: {
        panelClass: { type: [String, Object, Array], default: '' },
        contentClass: { type: [String, Object, Array], default: '' },
        resizeDirections: { type: String, default: 'all' },
    },
    computed: {
        activeResizeDirections() {
            const val = this.resizeDirections
            if (val === 'all') return ALL_DIRECTIONS
            if (val === 'horizontal') return ['e', 'w']
            if (val === 'vertical') return ['n', 's']
            if (val === 'none') return []
            return val.split(',').map(s => s.trim()).filter(s => ALL_DIRECTIONS.includes(s))
        },
    },
    methods: {
        openSettingsMenu(event) {
            this.$refs.settingsMenu?.open(event)
        },
        closeSettingsMenu() {
            this.$refs.settingsMenu?.close()
        },
        isSettingsMenuOpen() {
            return this.$refs.settingsMenu?.visible ?? false
        },
        _isInScrollableArea(target) {
            let el = target
            const boundary = this.$refs.panelContent
            while (el && el !== boundary) {
                if (el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth) {
                    const style = getComputedStyle(el)
                    if (['auto', 'scroll'].includes(style.overflowY) || ['auto', 'scroll'].includes(style.overflowX)) return true
                }
                el = el.parentElement
            }
            return false
        },
    },
}
</script>
